import { takeEvery, put, select } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import i18next from 'i18next'
import { List, Map } from 'immutable'

import { SELECTION, PROCEED } from 'app/constants/stages'
import { userInfoFromHash } from 'store/entities/router/selectors'
import { register, sendError } from 'store/entities/analytics/actions'
import { ERRORS } from 'store/entities/analytics/consts'
import { isResponseFailed } from 'helpers/responseHelpers'
import { showTypeFormPopup } from 'store/entities/typeform/actions'
import {
  activityActions,
  authLogin,
  getIdentityProgress,
  getProfile,
  subscribeToProTrack,
  updateSubscribeToProTrack,
  transferUserToOtherOrg,
} from '../actions'
import {
  setConfirmationModal,
  setQueries,
  setConfirmationModalForStopProTrack,
  setConfirmationModalForDifferentProTrack,
  setConfirmationModalForCommitPreviousActivity,
} from 'store/entities/localSettings/actions'
import {
  getIdentityStage,
  isBeginSurveySubmitted,
  isEndSurveySubmitted,
  getIdentityActiveProTrackId,
  getOnboardingNewProTrackLevels,
  getCurrentProTrackProgressDay,
  isIdentityActiveProTrackAutoEnroll,
  isStageIsSelection,
  getIdentityCanCompletePreviousActivityInWorkday,
} from '../selectors'
import { showInfo } from 'store/entities/toastr/utils'
import { WORK_WEEK_EVERY_OTHER_DAY, WORK_WEEK_ONCE_A_WEEK, WORK_WEEK_TWICE_A_WEEK } from 'app/constants/profile'
import { COMMIT } from 'app/constants/identityActions'
import { saveProfile } from 'store/entities/identity/actions'

export function* authLoginSuccessHandler({ payload }) {
  try {
    const query = yield select(userInfoFromHash)
    if (!query || !query.has('token') || !query.has('uid')) {
      yield put(
        sendError({
          type: ERRORS.AUTH_LOGIN_SUCCESS_HANDLER,
          error: new Error('query is wrong'),
          query,
        })
      )
      yield put(push('/'))
      return
    }

    // 1. register with analytics
    const { first_login, id, isLoginFlowFinished } = payload
    yield put(register(id, first_login))

    // 2. get profile
    const resProfile = yield getProfile.call()
    if (isResponseFailed(resProfile)) {
      throw new Error('Get Profile Error')
    }

    // 3. get progress
    const resProgress = yield getIdentityProgress.call()
    if (isResponseFailed(resProgress)) {
      throw new Error('Get Progress Error')
    }

    const stage = yield select(getIdentityStage)

    const proTrackLevelId = parseInt(query.get('protracklevelid'))

    // 4. Invitation
    const isInvitation = query.get('invitation')
    if (isInvitation) {
      // 4.a if clicks on invitation email or text message 2nd or more times
      if (!first_login) {
        const activeLevelId = yield select(getIdentityActiveProTrackId)
        const isAutoEnroll = yield select(isIdentityActiveProTrackAutoEnroll)
        // from email
        if (proTrackLevelId) {
          if (isLoginFlowFinished) {
            const newProTracksLevels = yield select(getOnboardingNewProTrackLevels)
            const proTrackName = newProTracksLevels
              .filter(el => el.get('id') === proTrackLevelId)
              .getIn(['0', 'proTrackName'])
            if (proTrackLevelId === activeLevelId) {
              showInfo({ content: i18next.t('Welcome back! You are on', { proTrackName }) })
            } else {
              const currentProTrackProgressDay = yield select(getCurrentProTrackProgressDay)
              const isSelection = yield select(isStageIsSelection)
              if (currentProTrackProgressDay < 2 && isAutoEnroll && !isSelection) {
                yield put(updateSubscribeToProTrack({ proTrackLevelId }))
              }
              if (isSelection) {
                yield put(subscribeToProTrack({ proTrackLevelId }))
              } else {
                yield put(setQueries({ proTrackLevelId, proTrackName }))
                // need update unsubscribe for subscribing after unsubscribed
                yield put(setConfirmationModal(true))
              }
            }
          } else {
            yield put(setQueries({ proTrackLevelId }))
          }
        }

        // from phone
        if (!proTrackLevelId && ((activeLevelId && isAutoEnroll) || !isLoginFlowFinished)) {
          yield put(push('/on-boarding-landing'))
          return
        }
      }

      // 4.b if user clicks on invitation email or text message first time
      if (first_login) {
        // show on-boarding landing page if user came from invitation text message
        if (!proTrackLevelId) {
          yield put(push('/on-boarding-landing'))
          return
        }
        // save proTrackLevelId if user came from invitation email and subscribe after OnBoardingNotificationSettingsModal
        if (proTrackLevelId) {
          yield put(setQueries({ proTrackLevelId }))
        }
      }
    }

    const typeFormLink = query.get('typeFormLink')
    const isBeginSubmitted = yield select(isBeginSurveySubmitted)
    const isEndSubmitted = yield select(isEndSurveySubmitted)
    if (typeFormLink) {
      const formLink = atob(typeFormLink)
      if (
        (formLink.indexOf('type=begin') !== -1 || formLink.indexOf('type=end') !== -1) &&
        (isBeginSubmitted || isEndSubmitted)
      ) {
        // if user has already sent beginSurvey or endSurvey
        showInfo({ content: 'You have already used this link to take the quiz' })
      } else {
        yield put(showTypeFormPopup({ typeFormLink: formLink }))
      }
    }

    // 5. handle selectNewProTrack if exists or redirect
    const isSelectNewProTrack = query.get('selectnewprotrack')
    const redirect = query.get('redirect')
    if (stage === SELECTION && isSelectNewProTrack) {
      yield put(push('/select-protrack'))
      showInfo({ content: 'Please continue selecting your ProTrack' })
      return
    } else if (redirect) {
      yield put(push({ pathname: `/${redirect}`, state: { autoPlay: !!query.get('autoPlay') } }))
      return
    }

    // 6. handle stop protrack from email
    const isStopProtrack = query.get('stopProtrack')
    if (stage === PROCEED && isStopProtrack) {
      yield put(setConfirmationModalForStopProTrack(true))
      yield put(push('/dashboard'))
      return
    }

    // 7. handle different protrack from email
    const isDifferentProTrack = query.get('differentProtrack')
    if (stage === PROCEED && isDifferentProTrack) {
      yield put(setConfirmationModalForDifferentProTrack(true))
    }

    // 8. Transfer user to another organization
    const isTransfer = query.get('transfer')
    const transferTo = query.get('transferTo')

    if (isTransfer) {
      if (proTrackLevelId) {
        // from email
        yield put(setQueries({ transferTo, proTrackLevelId }))
      } else {
        // from phone
        yield put(setQueries({ transferTo }))
      }
      yield put(transferUserToOtherOrg({ organizationId: transferTo }))
    }

    // 9. handle action if exists
    // Must be as like last action.
    // We will be have bug with rate and leave story for ast activity in ProTrackLevel if getIdentityProgress will be after activityActions[did]
    const action = query.get('action')
    const activityId = parseInt(query.get('activityId'))
    const userActivityId = parseInt(query.get('userActivityId'))
    const canCompletePreviousActivity = yield select(getIdentityCanCompletePreviousActivityInWorkday)
    if (action && activityId) {
      if (!activityActions[action]) {
        throw new Error(`Unexpected action with name: ${action}`)
      }
      if (canCompletePreviousActivity && action === COMMIT) {
        yield put(setConfirmationModalForCommitPreviousActivity(true))
      } else {
        const commitResponse = yield activityActions[action].call({ activityId, userActivityId, actionSource: 'email' })
        if (isResponseFailed(commitResponse)) {
          yield getProfile.call()
          yield getIdentityProgress.call()
          throw new Error('Commit error')
        }
      }
    }
    // 10. set new work week
    const changeNotificationFrequencyType = query.get('frequencyType')
    if (changeNotificationFrequencyType) {
      let workWeek
      switch (changeNotificationFrequencyType) {
        case 'everyOtherDay':
          workWeek = WORK_WEEK_EVERY_OTHER_DAY
          break
        case 'twiceAWeek':
          workWeek = WORK_WEEK_TWICE_A_WEEK
          break
        case 'onceAWeek':
          workWeek = WORK_WEEK_ONCE_A_WEEK
          break
      }
      if (!workWeek) {
        throw new Error(`Unexpected work week: ${workWeek}`)
      }
      yield put(saveProfile(Map({ workWeek: List(workWeek) })))
      yield put(push('/profile/notifications'))
      return
    }

    // 11. Login from sms activity notification
    const actionSource = query.get('actionSource')
    if (actionSource === 'sms') {
      yield put(push('/dashboard?actionSource=sms'))
      return
    }

    yield put(push('/dashboard'))
  } catch (error) {
    yield put(
      sendError({
        type: ERRORS.AUTH_LOGIN_SUCCESS_HANDLER,
        error,
      })
    )
    yield put(push('/'))
  }
}

export default function* authLoginSuccessEvery() {
  yield takeEvery(authLogin.success.toString(), authLoginSuccessHandler)
}
